// Lichte i18n hook — geen externe dependency.
// Gebruik: const t = useT(); t('confirm.delete.title')
// Onbekende sleutels retourneren de sleutel zelf als fallback.
import { useCallback, useSyncExternalStore } from 'react';
import nl from './nl';
import en from './en';

const translations = { nl, en };
const LOCALE_STORAGE_KEY = 'todoes.locale';

const localeListeners = new Set();
let localeNotifyScheduled = false;

// Defer subscriber updates so setLocale() from test teardown (afterEach) runs after
// RTL unmount/cleanup; skip when the effective locale is unchanged.
function notifyLocaleChange() {
  if (localeNotifyScheduled) return;
  localeNotifyScheduled = true;
  queueMicrotask(() => {
    localeNotifyScheduled = false;
    localeListeners.forEach((listener) => listener());
  });
}

function subscribeLocale(listener) {
  localeListeners.add(listener);
  return () => localeListeners.delete(listener);
}

// Voorkeur: opgeslagen gebruikerskeuze > browsertaal > 'nl'.
// Guards ontbrekende globals (SSR/node-test-omgevingen zonder jsdom).
function getLocale() {
  const stored =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem(LOCALE_STORAGE_KEY)
      : null;
  if (stored && translations[stored]) return stored;
  const browserLang =
    typeof navigator !== 'undefined' ? navigator.language?.slice(0, 2) : null;
  return translations[browserLang] ? browserLang : 'nl';
}

function getLocaleSnapshot() {
  return getLocale();
}

function getLocaleServerSnapshot() {
  return 'nl';
}

export function setLocale(locale) {
  if (!translations[locale]) return;
  if (getLocale() === locale) {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    }
    return;
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }
  notifyLocaleChange();
}

export function getCurrentLocale() {
  return getLocale();
}

function lookupString(locale, key, fallback, params) {
  const strings = translations[locale] || translations.nl;
  let result = strings[key] !== undefined ? strings[key] : fallback || key;

  if (params && typeof params === 'object') {
    Object.keys(params).forEach((paramKey) => {
      result = result.replace(
        new RegExp(`\\{${paramKey}\\}`, 'g'),
        params[paramKey],
      );
    });
  }

  return result;
}

// Niet-hook variant voor plekken die geen hooks mogen aanroepen (class components,
// module-scope code). Zelfde lookup, geen React-afhankelijkheid.
export function translate(key, fallback, params) {
  return lookupString(getLocale(), key, fallback, params);
}

export function useCurrentLocale() {
  return useSyncExternalStore(
    subscribeLocale,
    getLocaleSnapshot,
    getLocaleServerSnapshot,
  );
}

/**
 * @returns {(key: string, fallback?: string, params?: Record<string, string | number>) => string}
 */
export function useT() {
  const locale = useCurrentLocale();
  return useCallback(
    (key, fallback, params) => lookupString(locale, key, fallback, params),
    [locale],
  );
}

export default useT;
